import React, { KeyboardEvent, ReactNode } from "react"
import {
  Box, Button, ButtonProps, SimpleGrid,
} from "@chakra-ui/react"
import {
  Control, FieldValues, FieldPath, useController,
} from "react-hook-form"

type ItemProps = Omit<ButtonProps, "onClick" | "children" | "title" | "variant"> & {
  selected: boolean
  onClick: () => void
  children: ReactNode
  title?: string
  // When provided, tints selected/hover with this raw color (e.g. user-picked hex)
  // instead of using the Chakra colorScheme tokens.
  tintColor?: string
}

export const ToggleButtonGroupItem = ({
  selected,
  onClick,
  children,
  title,
  size = "sm",
  w = "full",
  p = 2,
  colorScheme = "blue",
  tintColor,
  ...rest
}: ItemProps) => {
  // For raw hex (tintColor), mimic Chakra's .50 shade by mixing the color
  // with white at a low ratio — gives a pale wash that reads cleanly against
  // a white background, similar to how `blue.50` looks vs `blue.500`.
  const selectedBg = tintColor
    ? `color-mix(in srgb, ${tintColor} 8%, white)`
    : `${colorScheme}.50`
  const selectedBorder = tintColor || `${colorScheme}.200`
  const selectedFg = tintColor || `${colorScheme}.600`

  return (
    <Button
      size={size}
      variant="outline"
      role="radio"
      aria-checked={selected}
      // Roving tabindex: only the selected item participates in the page tab
      // order. Arrow keys (handled by the parent ToggleButtonGroup) move
      // focus between items inside the group.
      tabIndex={selected ? 0 : -1}
      backgroundColor={selected ? selectedBg : "white"}
      borderColor={selected ? selectedBorder : "gray.200"}
      color={selected ? selectedFg : "gray.700"}
      w={w}
      p={p}
      title={title}
      onClick={onClick}
      {...rest}
      _active={{
        backgroundColor: selectedBg,
        borderColor: selected ? selectedBorder : "gray.200",
        color: selected ? selectedFg : "gray.700",
      }}
      _hover={{
        backgroundColor: selected ? selectedBg : "gray.50",
        borderColor: selected ? selectedBorder : "gray.300",
      }}
    >
      {children}
    </Button>
  )
}

type Props<T extends FieldValues = FieldValues> = {
  name: FieldPath<T>
  control: Control<T>
  children: {
    button: (selected: boolean, onClick: () => void) => ReactNode
    value: string
    disabled?: boolean
  }[]
  columns?: number
  spacing?: number
}

const ARROW_KEYS = ["ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown", "Home", "End"]

const ToggleButtonGroup = <T extends FieldValues = FieldValues>({
  name, control, children, columns = 3, spacing = 4,
}: Props<T>) => {
  const { field } = useController<T, FieldPath<T>>({ control, name })

  // Native radio-group keyboard nav. Arrow / Home / End walks the value and
  // moves focus to the corresponding child rendered with `role="radio"`.
  // Disabled children are skipped so keyboard users can't select an unavailable option.
  const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (!ARROW_KEYS.includes(e.key)) return
    e.preventDefault()
    const enabledValues = children.filter((c) => !c.disabled).map((c) => c.value)
    if (enabledValues.length === 0) return
    const idx = enabledValues.indexOf(field.value as string)
    const last = enabledValues.length - 1
    let next = idx < 0 ? 0 : idx
    if (e.key === "ArrowLeft" || e.key === "ArrowUp") next = idx <= 0 ? last : idx - 1
    else if (e.key === "ArrowRight" || e.key === "ArrowDown") next = idx === last ? 0 : idx + 1
    else if (e.key === "Home") next = 0
    else if (e.key === "End") next = last
    const nextValue = enabledValues[next]
    if (nextValue === field.value) return
    field.onChange(nextValue)
    const domIdx = children.findIndex((c) => c.value === nextValue)
    const radios = e.currentTarget.querySelectorAll<HTMLButtonElement>("[role=\"radio\"]")
    radios[domIdx]?.focus()
  }

  return (
    <SimpleGrid
      mb={0}
      spacing={spacing}
      columns={columns}
      role="radiogroup"
      onKeyDown={handleKeyDown}
    >
      {children.map(({ button, value }) => {
        const selected = field.value === value

        return (
          <Box key={value}>
            {button(selected, () => field.onChange(value))}
          </Box>
        )
      })}
    </SimpleGrid>
  )
}

export default ToggleButtonGroup
